import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Calendar,
  BarChart3,
  PieChart
} from 'lucide-react';

function SubscriptionAnalyticsPage() {
  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ['subscriptionAnalytics'],
    queryFn: async () => {
      const response = await api.get('/admin/subscriptions/analytics');
      return response.data.data; // Backend returns { success, data }
    }
  });

  if (isLoading) return <div className="p-6 text-center">Loading analytics...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error.message}</div>;

  const { 
    statusDistribution, 
    monthlyRecurringRevenue, 
    trialConversionRate, 
    churnRate, 
    planDistribution 
  } = analytics;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Subscription Analytics</h1>
        <p className="text-gray-600">Comprehensive insights into subscription performance and metrics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly Recurring Revenue</p>
              <p className="text-2xl font-bold text-green-600">
                ${monthlyRecurringRevenue?.toFixed(2) || '0.00'}
              </p>
            </div>
            <DollarSign className="text-green-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Trial Conversion Rate</p>
              <p className="text-2xl font-bold text-blue-600">
                {trialConversionRate?.toFixed(1) || '0.0'}%
              </p>
            </div>
            <TrendingUp className="text-blue-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Churn Rate</p>
              <p className="text-2xl font-bold text-red-600">
                {churnRate?.toFixed(1) || '0.0'}%
              </p>
            </div>
            <TrendingDown className="text-red-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Active Subscriptions</p>
              <p className="text-2xl font-bold text-purple-600">
                {statusDistribution?.find(s => s.status === 'active')?.count || 0}
              </p>
            </div>
            <Users className="text-purple-500" size={24} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Status Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <PieChart className="mr-2" size={20} />
            Subscription Status Distribution
          </h2>
          <div className="space-y-3">
            {statusDistribution?.map((status) => (
              <div key={status.status} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    status.status === 'active' ? 'bg-green-500' :
                    status.status === 'trialing' ? 'bg-blue-500' :
                    status.status === 'past_due' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}></div>
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {status.status.replace('_', ' ')}
                  </span>
                </div>
                <span className="text-sm font-bold text-gray-900">{status.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <BarChart3 className="mr-2" size={20} />
            Active Plan Distribution
          </h2>
          <div className="space-y-3">
            {planDistribution?.map((plan) => (
              <div key={plan.plan_name} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{plan.plan_name}</p>
                  <p className="text-xs text-gray-500">${plan.price}/month</p>
                </div>
                <span className="text-sm font-bold text-gray-900">{plan.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Insights */}
      <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Calendar className="mr-2" size={20} />
          Revenue Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">Monthly Recurring Revenue</p>
            <p className="text-2xl font-bold text-green-600">
              ${monthlyRecurringRevenue?.toFixed(2) || '0.00'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Average Revenue Per User</p>
            <p className="text-2xl font-bold text-blue-600">
              ${planDistribution?.length > 0 ? 
                (monthlyRecurringRevenue / planDistribution.reduce((sum, plan) => sum + plan.count, 0)).toFixed(2) : 
                '0.00'
              }
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Annual Recurring Revenue</p>
            <p className="text-2xl font-bold text-purple-600">
              ${((monthlyRecurringRevenue || 0) * 12).toFixed(2)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionAnalyticsPage;
